/**
 * Authentication Service for MVP
 * Handles login, token management, and user session
 */

import { syncPFUserUuidToLocalStorage } from '../utils/jwtHelper';

import { SecureStorage } from '../utils/SecureStorage';
// Empty-string fallback (not an absolute localhost URL) - matches the relative-URL
// convention used by VITE_API_BASE_URL elsewhere (see useDirectoryButtons.ts,
// DirectoryRenderer.tsx). In local dev with no VITE_API_URL override, this makes
// fetch('/api/auth/login') same-origin, routed through vite.config.ts's server.proxy
// to the production VM (no CORS). Production always sets an explicit absolute
// VITE_API_URL via .env.production, so this fallback never triggers there.
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface LoginRequest {
    username: string;
    password: string;
}

export interface LoginResponse {
    success: boolean;
    message?: string;
    detail?: string;
    access_token?: string;
    refresh_token?: string;
    token_type?: string;
    user_uuid?: string;
    pfuser_id?: number;
    username?: string;
}

export interface ExecutiveAccessResponse {
    has_access: boolean;
    access_type?: number;
    access_type_name?: string;
}

/**
 * Login with username and password
 * Stores tokens in localStorage on success
 */
export const loginRequest = async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
        });

        const data = await response.json();

        if (response.ok && data.access_token) {
            // Store tokens
            SecureStorage.setAccessToken(data.access_token);
            if (data.refresh_token) {
                SecureStorage.setRefreshToken(data.refresh_token);
            }
            if (data.user_uuid) {
                SecureStorage.setUserUuid(data.user_uuid);
            }
            if (data.pfuser_id) {
                SecureStorage.setPfuserId(data.pfuser_id); // SecureStorage handles toString() internally
            }
            if (data.username) {
                SecureStorage.setUsername(data.username);
            }

            // ✅ CRITICAL: Extract pfuser_uuid from JWT token and sync to localStorage
            // This ensures all components can access pfuser_uuid consistently
            syncPFUserUuidToLocalStorage();

            return {
                success: true,
                message: data.message || 'Login successful',
                ...data,
            };
        }

        return {
            success: false,
            detail: data.detail || 'Login failed',
        };
    } catch (error) {
        console.error('[authService] Login error:', error);
        return {
            success: false,
            detail: error instanceof Error ? error.message : 'Network error',
        };
    }
};

/**
 * Check if user has executive access
 */
export const checkExecutiveAccess = async (username: string): Promise<ExecutiveAccessResponse> => {
    try {
        const token = SecureStorage.getAccessToken();
        if (!token) {
            return { has_access: false };
        }

        const response = await fetch(`${API_BASE_URL}/api/auth/executive-access/${username}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (response.ok) {
            const data = await response.json();
            return data;
        }

        return { has_access: false };
    } catch (error) {
        console.error('[authService] Executive access check error:', error);
        return { has_access: false };
    }
};

/**
 * Logout - clear tokens and session data
 */
export const logout = () => {
    // Clear all auth state (tokens + user context)
    SecureStorage.clearAuth();

    // Clear non-auth localStorage items
    localStorage.removeItem('exec_access_type');
    localStorage.removeItem('exec_access_name');
    localStorage.removeItem('preferred_landing_page');
    localStorage.removeItem('show_org_request_modal');
};
